import { Switch } from '@syncfusion/ej2-buttons';

import { FormFieldType } from '@/shared/types/enum';
import { FormField, FormFieldTypesWithOptions } from '@/shared/types/forms';
import { Fragment, elements } from '@/shared/types/html';
import { formatEnum } from '@/shared/utils/format';
import { classes } from '@/shared/utils/style';

import { handleFormPostResponse } from '@/front/editable/state';
import { POST } from '@/front/fetch';
import { onFormSave } from '@/front/formSave';
import { alertModal } from '@/front/modal';
import { initTableRowReorder } from '@/front/reorder';
import { initElements, onLoad } from '@/front/utils/dom';

onLoad(() => {
  initElements<HTMLDivElement>(document, '.form-editor', (editorDiv) => {
    const { form, fieldsInput, fieldsDiv } = getElements(editorDiv);

    onFormSave(form, async () => {
      if (updateFieldsInput(editorDiv, true)) {
        const data = Object.fromEntries(new FormData(form).entries());
        const res = await POST(form.action, data);

        await handleFormPostResponse(form, res);
      }
    });

    form.addEventListener('input', () => {
      form.dataset.dirty = 'true';
    });

    const fields: FormField[] = JSON.parse(fieldsInput.value);

    fieldsDiv.innerHTML = <FieldTable fields={fields} />;

    initElements<HTMLTableRowElement>(fieldsDiv, 'tbody tr', (tr) => initFieldRow(editorDiv, tr));

    initElements<HTMLAnchorElement>(fieldsDiv, 'a[data-action=addField]', (a) => {
      a.addEventListener('click', () => {
        const field: FormField = {
          name: '',
          label: '',
          type: FormFieldType.Text,
          required: true,
        };
        const row = document.createElement('tr');
        row.innerHTML = <FieldRow field={field} />;
        a.closest('table')?.querySelector('tbody')?.appendChild(row);
        initFieldRow(editorDiv, row);
      });
    });
  });
});

const getElements = (editorDiv: HTMLDivElement) => ({
  form: editorDiv.closest('form') as HTMLFormElement,
  fieldsInput: editorDiv.querySelector('input[name=fields]') as HTMLInputElement,
  fieldsDiv: editorDiv.querySelector('.form-editor-fields') as HTMLDivElement,
});

const getFieldRowElements = (tr: HTMLTableRowElement) => ({
  nameInput: tr.querySelector('input[data-prop=name]') as HTMLInputElement,
  labelInput: tr.querySelector('input[data-prop=label]') as HTMLInputElement,
  typeSelect: tr.querySelector('select[data-prop=type]') as HTMLSelectElement,
  requiredInput: tr.querySelector('input[data-prop=required]') as HTMLInputElement,
  optionsTextArea: tr.querySelector('textarea[data-prop=options]') as HTMLTextAreaElement,
  deleteButton: tr.querySelector('a[data-action=deleteField]') as HTMLAnchorElement,
});

const initFieldRow = (editorDiv: HTMLDivElement, tr: HTMLTableRowElement) => {
  const el = getFieldRowElements(tr);

  el.typeSelect.addEventListener('change', () => {
    const enabled = FormFieldTypesWithOptions.includes(el.typeSelect.value as FormFieldType);
    el.optionsTextArea.classList.toggle('d-none', !enabled);
    el.optionsTextArea.required = enabled;
  });

  const swtch = new Switch({ checked: el.requiredInput.checked });
  swtch.appendTo(el.requiredInput);
  swtch.addEventListener('change', () => updateFieldsInput(editorDiv));

  el.nameInput.addEventListener('blur', () => updateFieldsInput(editorDiv));
  el.labelInput.addEventListener('blur', () => updateFieldsInput(editorDiv));
  el.typeSelect.addEventListener('blur', () => updateFieldsInput(editorDiv));
  el.optionsTextArea.addEventListener('blur', () => updateFieldsInput(editorDiv));

  el.deleteButton.addEventListener('click', () => {
    const tr = el.deleteButton.closest('tr') as HTMLTableRowElement;
    tr.remove();
    updateFieldsInput(editorDiv);
  });

  initTableRowReorder(tr, {
    onReorder: () => {
      updateFieldsInput(editorDiv);
    },
  });
};

const updateFieldsInput = (editorDiv: HTMLDivElement, validate = false) => {
  const { form, fieldsInput, fieldsDiv } = getElements(editorDiv);

  const fields = [...fieldsDiv.querySelectorAll('tbody tr')].map((tr) => {
    const el = getFieldRowElements(tr as HTMLTableRowElement);

    const options = el.optionsTextArea.value
      .split('\n')
      .map((option) => option.trim())
      .filter((option) => option);

    return {
      name: el.nameInput.value.trim(),
      type: el.typeSelect.value as FormFieldType,
      label: el.labelInput.value.trim(),
      options: options.length ? options : undefined,
      required: el.requiredInput.checked,
    } satisfies FormField;
  });

  if (validate) {
    if (!form.checkValidity()) {
      form.reportValidity();
      return false;
    }

    const names = fields.map((field) => field.name);

    if (new Set(names).size !== names.length) {
      alertModal('One or more fields have duplicate names. Please ensure each field has a unique name.');
      return false;
    }
  }

  fieldsInput.value = JSON.stringify(fields);

  return true;
};

const FieldTable = ({ fields }: { fields: FormField[] }) => (
  <>
    <table class="table">
      <thead>
        <tr>
          <th class="px-2 text-muted"></th>
          <th class="px-2 text-muted">Name</th>
          <th class="px-2 text-muted">Label</th>
          <th class="px-2 text-muted">Type</th>
          <th class="px-2 text-muted">Required</th>
          <th class="px-2 text-muted"></th>
        </tr>
      </thead>
      <tbody>
        {fields.map((field) => (
          <FieldRow field={field} />
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colspan={6} class="text-center">
            <a data-action="addField" class="button btn btn-link">
              <i class="fa-solid fa-plus me-2" />
              Add Field
            </a>
          </td>
        </tr>
      </tfoot>
    </table>
  </>
);

const FieldRow = ({ field }: { field: FormField }) => (
  <tr draggable="true">
    <td class="align-top">
      <a class="button btn btn-link reorder">
        <i class="fa-solid fa-grip-dots-vertical"></i>
      </a>
    </td>
    <td class="align-top">
      <FieldName field={field} />
    </td>
    <td class="align-top">
      <FieldLabel field={field} />
    </td>
    <td class="align-top">
      <FieldType field={field} />
      <FieldOptions field={field} />
    </td>
    <td class="align-top">
      <div class="d-flex flex-column gap-2">
        <FieldValidation field={field} />
      </div>
    </td>
    <td class="align-top">
      <FieldDelete />
    </td>
  </tr>
);

const FieldName = ({ field }: { field: FormField }) => (
  <input
    data-prop="name"
    type="text"
    value={field.name}
    placeholder="e.g. my_field"
    class="form-control"
    required="required"
    pattern="[A-Za-z0-9_]+"
    title="Only letters, numbers, and underscores are allowed (e.g. my_field)."
  />
);

const FieldType = ({ field }: { field: FormField }) => (
  <select data-prop="type" class="form-select">
    {Object.values(FormFieldType).map((type) => (
      <option value={type} {...(field.type === type ? { selected: 'selected' } : {})}>
        {formatEnum(FormFieldType, type)}
      </option>
    ))}
  </select>
);

const FieldLabel = ({ field }: { field: FormField }) => (
  <input
    data-prop="label"
    type="text"
    value={field.label}
    placeholder="e.g. My Field"
    class="form-control"
    required="required"
  />
);

const FieldValidation = ({ field }: { field: FormField }) => (
  <div class="d-flex align-items-center mt-2">
    <input data-prop="required" type="checkbox" {...(field.required ? { checked: 'checked' } : {})} />
  </div>
);

const FieldOptions = ({ field }: { field: FormField }) => {
  const enabled = FormFieldTypesWithOptions.includes(field.type);
  return (
    <textarea
      data-prop="options"
      placeholder={['Option 1', 'Option 2', 'Option 3'].join('\n')}
      rows="5"
      class={classes('form-control', 'mt-2', !enabled && 'd-none')}
      {...(enabled ? { required: 'required' } : {})}
    >
      {(field.options ?? []).join('\n')}
    </textarea>
  );
};

const FieldDelete = () => (
  <a data-action="deleteField" class="button btn btn-link">
    <i class="fa-solid fa-trash" />
  </a>
);
